import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, RETURN_ADDRESS, CARD_DESCRIPTOR } from '../data/mock';
import { FileText } from 'lucide-react';

const TermsPage = () => {
  useEffect(() => {
    document.title = 'Terms & Conditions | Radiant Bliss Aura';
  }, []);

  const corp = CONTACT_INFO.corpName;
  const domain = CONTACT_INFO.domain;
  const phone = CONTACT_INFO.phone;
  const email = CONTACT_INFO.email;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />

      <main className="pt-[104px]">
        {/* Banner */}
        <section className="bg-[#faf7f2] border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white border border-athletic-200 rounded-full mb-6">
              <FileText className="w-3.5 h-3.5 text-athletic-500" />
              <span className="text-athletic-700 text-[11px] font-medium tracking-[0.18em] uppercase">Legal</span>
            </div>
            <h1 className="text-5xl lg:text-6xl text-[#1a1a1a] mb-4"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, letterSpacing: '-0.02em' }}>
              Terms &amp; <span className="italic text-athletic-500">Conditions</span>
            </h1>
            <p className="text-[#1a1a1a]/65 max-w-xl leading-relaxed">
              Please read these terms carefully before placing an order or using our services.
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="py-20 bg-white">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <p className="text-[#1a1a1a]/60 text-sm mb-2">
              <span className="font-medium text-[#1a1a1a]">Effective date:</span>{' '}
              {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>

            <p className="text-[#1a1a1a]/75 leading-relaxed text-base mb-10">
              By placing an order through <span className="text-athletic-600 font-medium">{domain}</span>, you agree to the
              terms and conditions set forth below. Please read through these terms carefully before placing your order and print a
              copy for future reference. Please also read our Privacy Policy regarding personal information provided by you, which is
              incorporated herein by reference. This Agreement is between {corp} and you (&ldquo;you&rdquo;, &ldquo;your&rdquo; or
              &ldquo;Customer&rdquo;). This Section sets forth the terms and conditions which apply to the use by you of {corp} Sites
              (as defined below) and any other subscription product or service offered for sale by {corp} and/or its affiliates
              (collectively, &ldquo;{corp}&rdquo;). The right to use any product or service offered by {corp} is personal to you and
              is not transferable to any other person or entity. {corp} reserves the right to make changes to {corp} sites, policies,
              and these Terms at any time without notice. By using, accessing or ordering products from {domain}, you hereby agree to
              all of the following terms.
            </p>

            {/* Order summary */}
            <div className="bg-[#faf7f2] border border-gray-100 rounded-2xl p-6 mb-12">
              <p className="text-[#1a1a1a]/75 leading-relaxed text-sm">
                Should your card decline we reserve the right to offer you the product at a discounted rate. Charges will appear as
                {' '}<span className="text-athletic-600 font-medium">{CARD_DESCRIPTOR}</span> on your billing statement. We ship the
                product the day after you place your order (except that orders placed Saturday&ndash;Sunday will be shipped the
                following Monday). Shipping takes from 3 to 5 business days within the USA via USPS. If you are unhappy with the
                product for any reason, you must either call us at {phone} or send an email to{' '}
                <a href={`mailto:${email}`} className="text-athletic-600 hover:text-athletic-700 font-medium">{email}</a>{' '}
                &mdash; we have a 30-day money-back guarantee.
              </p>
              <p className="text-[#1a1a1a]/75 leading-relaxed text-sm mt-3">
                Our Customer Service Department is open Monday through Friday, 8&nbsp;am&nbsp;&ndash;&nbsp;8&nbsp;pm and Saturday
                9&nbsp;am&nbsp;&ndash;&nbsp;5&nbsp;pm EST. {phone}
              </p>
            </div>

            <Section number="1" title={`Use of ${corp} Sites`}>
              <p>
                {corp} Sites contain links to other Web sites, resources and advertisers. {corp} is not responsible for the availability
                of these external sites nor does it endorse or is it responsible for the contents, advertising, products or other
                materials made available on or through such external sites. Under no circumstances shall {corp} be held responsible
                or liable, directly or indirectly, for any loss or damage caused or alleged to have been caused to a user in
                connection with the use of or reliance on any content, goods or services available on such external site. You should
                direct any concerns to such external site&rsquo;s administrator or webmaster.
              </p>
            </Section>

            <Section number="2" title="Disclaimer of Warranty; Limitation of Liability">
              <p>
                {corp} neither endorses nor is responsible for the accuracy or reliability of any opinion, advice or statement on
                {' '}{corp} sites, nor for any offensive, defamatory or obscene posting made by any user. Under no circumstances will
                {' '}{corp} be liable for any loss or damage caused by your reliance on information obtained through the content on
                {' '}{corp} sites. It is your responsibility to evaluate the accuracy, completeness or usefulness of any information,
                opinion, advice or other content available through {corp} sites. Please seek the advice of professionals, as
                appropriate, regarding the evaluation of any specific information, opinion, advice or other content, including but
                not limited to skincare, dermatology, health or lifestyle information.
              </p>
            </Section>

            <Section number="3" title="Indemnification">
              <p>
                You agree to defend, indemnify, and hold harmless {corp}, its officers, directors, shareholders, employees,
                independent contractors, telecommunication providers, and agents, from and against any and all claims, actions, loss,
                liabilities, expenses, costs, or demands, including without limitation legal and accounting fees, for all damages
                directly, indirectly, and/or consequentially resulting or allegedly resulting from your misuse or inability to use{' '}
                {domain}, or your breach of any of these terms and conditions of this Agreement. We shall promptly notify you by
                electronic mail of any such claim or suit, and cooperate fully (at your expense) in the defense of such claim or
                suit. If we do not hear from you promptly, we reserve the right to defend such claim or suit and seek full recompense
                from you.
              </p>
            </Section>

            <Section number="4" title="Shipping">
              <p>
                Our standard ground shipping takes from 3 to 5 business days within the continental United States via USPS. If you
                have not received your order within 21 days from your purchase date, please call our customer care center to request
                a reshipment of your order. If you entered the wrong address on our order form and we are forced to reship your
                package, there will be an additional shipping and handling charge equal to your original order. To manage your order,
                contact Phone: {phone}.
              </p>
            </Section>

            <Section number="5" title="Sales and Distribution">
              <p>
                Distributor will not sell any product on eBay, Amazon, any of their affiliate&rsquo;s websites or any other auction
                website or online marketplace. Prices do not include and are net of any governmental taxes or charges of any kind
                that may be applicable to the sale, licensing, marketing or distribution of the products. Distributor understands and
                acknowledges that all sales are final and that they are not entitled to return any product for a refund except as
                expressly provided in Section&nbsp;6 below. {corp} will not be responsible for any delays or lack of delivery due to
                customs clearance issues.
              </p>
            </Section>

            <Section number="6" title="Cancellation Policy &mdash; Cancellations &mdash; Billing Errors &mdash; Refunds &mdash; 30-Day Return Policy">
              <p>
                Once an order has been submitted your credit card will be charged. Our ordering process is streamlined so your order
                will immediately be sent to processing and then shipping. We take great pride in the superior quality of our products
                and want you to be pleased with your purchase. We believe in offering the very best value, quality and selection to
                our customers. To inquire on a refund, please contact {corp} at one of the following options:
              </p>
              <ul className="list-disc pl-6 my-3 space-y-1">
                <li>Phone: {phone}</li>
                <li>Email:{' '}
                  <a href={`mailto:${email}`} className="text-athletic-600 hover:text-athletic-700 font-medium">{email}</a>
                </li>
              </ul>
              <p>
                If you are ever not satisfied with any product that you ordered, you may call our Customer Service Department and
                request a refund. You may receive a refund of any product that you ordered up to thirty (30) days after your order
                was placed. Customers will receive a single refund per product ordered, and repetitive refunds are not permitted
                unless the product, as delivered to you, is defective. {corp} reserves the right to refuse a refund to any customer
                who repeatedly requests refunds or who, in {corp}&rsquo;s judgment, requests refunds in bad faith. In order to
                process your refund, you must supply us with your name and delivery address. If you provide us with incorrect
                information, your refund will be delayed. Refunds can take up to thirty (30) days to appear on your credit card
                statement depending on the bank that issued your credit card. If you have any questions about whether a refund has
                been issued, please call our Customer Service Department.
              </p>

              <h3 className="text-xl text-[#1a1a1a] mt-8 mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                Billing Errors
              </h3>
              <p>If you believe that you have been billed improperly, please notify our Customer Service Department immediately.</p>

              <h3 className="text-xl text-[#1a1a1a] mt-8 mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                Returns
              </h3>
              <p>
                In order to obtain your refund, contact customer service by phone and obtain an RMA (Return Merchandise
                Authorization) number to place on your package. Write this number on the outside of the shipping package, and send
                the product back to our fulfillment center at the address listed below, within thirty (30) days of the date you
                originally ordered the product. In order for your refund to be processed the product must arrive at our fulfillment
                facility within thirty (30) days of the original purchase date. You pay for return shipping. There is a $5.95
                restocking fee per unit you are returning. This fee will be taken out of the refund issued. Once our fulfillment
                center has received the package and relayed the correct information to us, you will be issued a refund. Your refund
                will be credited back to your bank account, and may take up to 3&ndash;5 business days to show in your statement,
                depending on the speed of the processing bank. Please call customer service at {phone} for return address details,
                so we can make notes on your account.
              </p>

              <p className="mt-4 font-medium text-[#1a1a1a]">Please Note:</p>
              <ol className="list-decimal pl-6 my-3 space-y-1">
                <li>We cannot process or refund packages marked &ldquo;Return to Sender.&rdquo;</li>
                <li>Refunds will be issued to the same credit card that was charged when ordering the product.</li>
              </ol>

              <p className="mt-4">Returned products must be sent to the following address:</p>
              <div className="bg-[#faf7f2] border border-gray-100 rounded-2xl p-5 mt-3 mb-4 font-medium text-[#1a1a1a]">
                {RETURN_ADDRESS.street}<br />
                {RETURN_ADDRESS.city}, {RETURN_ADDRESS.state} {RETURN_ADDRESS.zip}
              </div>
              <p className="text-sm text-[#1a1a1a]/60">
                We are not responsible for lost or stolen items. We recommend all returned items be sent using some type of delivery
                confirmation system to ensure proper delivery.
              </p>
            </Section>

            <Section number="7" title="Miscellaneous">
              <p>
                This Agreement and all matters arising out of, or otherwise relating to, this Agreement shall be governed exclusively
                by the laws of the State of Illinois, excluding its conflict-of-law provisions. Venue for any matter arising from or
                related to this Agreement shall exist solely and exclusively in DuPage County, Illinois, and the parties hereby
                submit to the personal jurisdiction of the state and federal courts sitting in DuPage County, Illinois for such
                purpose. In the event any Party shall commence any claims, actions, formal legal action, or arbitration to interpret
                and/or enforce the terms and conditions of this Agreement, the prevailing party in any such action or proceeding
                shall be entitled to recover, in addition to all other available relief, its reasonable attorney&rsquo;s fees and
                costs incurred in connection therewith, including attorney&rsquo;s fees incurred on appeal. No waiver of or by {corp}
                shall be deemed a waiver of any subsequent default of the same provision of this Agreement. If any term, clause or
                provision hereof is held invalid or unenforceable by a court of competent jurisdiction, such invalidity shall not
                affect the validity or operation of any other term, clause or provision and such invalid term, clause or provision
                shall be deemed to be severed from this Agreement. {corp} shall not be responsible for any failure to perform due to
                unforeseen circumstances or to causes beyond our reasonable control, including but not limited to: acts of God, such
                as fire, flood, earthquakes, hurricanes, tropical storms or other natural disasters; war, riot, arson, embargoes,
                acts of civil or military authority, or terrorism; strikes, or shortages in transportation, facilities, fuel, energy,
                labor or materials; failure of the telecommunications or information services infrastructure; hacking, spam, or any
                failure of a computer, server or software, for so long as such event continues to delay {corp}&rsquo;s performance.
                All headings are solely for the convenience of reference and shall not affect the meaning, construction or effect of
                this Agreement. This Agreement constitutes the entire agreement between the parties with respect to your access and
                use of {domain} and your ordering and use of the products, and supersedes and replaces all prior understandings or
                agreements, written or oral, regarding such subject matters. {corp} reserves the right to change any of the
                provisions posted herein and you agree to review these terms and conditions each time you visit {domain}. Your
                continued use of {domain} following the posting of any changes to these terms and conditions constitutes your
                acceptance of such changes. {corp} does not and will not assume any obligation to provide you with notice of any
                change to this document. Unless accepted by {corp} in writing, these terms and conditions may not be amended by you.
              </p>
            </Section>

            <Section number="8" title="Skincare Safety Guidelines">
              <p>
                Our products are cosmetic skincare formulations intended for external use only. Always patch-test a new product on a
                small area of skin (such as the inner forearm) for 24&nbsp;hours before applying to the face or body, especially if
                you have sensitive skin or known allergies. Discontinue use immediately and consult a qualified medical professional
                if you experience irritation, redness, swelling, or any allergic reaction.
              </p>
              <p className="mt-3">
                Statements made by {corp} have not been evaluated by the Food and Drug Administration. These products are not intended
                to diagnose, treat, cure or prevent any illness or disease. If you are pregnant, nursing, or have a chronic medical
                condition, please consult your physician or dermatologist before using any new skincare product. Keep all products
                out of reach of children and avoid contact with eyes. Use only as directed on the product label.
              </p>
            </Section>

            <div className="mt-16 pt-8 border-t border-gray-100">
              <p className="text-sm text-[#1a1a1a]/50">
                Questions about these Terms? Contact us at{' '}
                <a href={`mailto:${email}`} className="text-athletic-600 hover:text-athletic-700 font-medium">{email}</a>{' '}
                or {phone}.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

const Section = ({ number, title, children }) => (
  <section className="mt-12 pt-8 border-t border-gray-100 first:border-t-0 first:pt-0 first:mt-0">
    <h2 className="text-2xl lg:text-3xl text-[#1a1a1a] mb-5 flex items-baseline gap-3"
        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
      <span className="text-athletic-500 text-base font-medium tracking-wider">{number}.</span>
      <span dangerouslySetInnerHTML={{ __html: title }} />
    </h2>
    <div className="text-[#1a1a1a]/75 leading-relaxed space-y-3 text-base">
      {children}
    </div>
  </section>
);

export default TermsPage;
